import type { ComputedRef } from 'vue'

export interface LoginCredentials {
  username: string
  password: string
}

export interface FeedbackForm {
  subject: string
  message: string
  email: string
}

export enum WhatNowContext {
  Home = 0,
  CurrentSituation = 1,
  Summary = 2,
  Causation = 3,
  CallToAction = 4,
  Profile = 5,
  History = 6,
  NewCallToAction = 7,
  Worklog = 8,
  Feedback = 9
}

export interface Analysis {
  id: string
  title: string
}

export interface Statement {
  id: string
  title: string
  description?: string
  order: number
  evaluation: number
  evaluated: ComputedRef<boolean>
}

export interface CausationStatementEntry {
  statement: Statement
  label: string
}

export interface CausationSummary {
  label: string
  statements: Statement[]
}

export interface User {
  id: number
  username: number
  email?: string
  organizationName: string
  teamName: string
  departmentName: string
  token: string
  password?: string
  campaignCode?: string
  currentOrganizationMembership: OrganizationMembership
  organizationMemberships: OrganizationMembership[]
}

export interface AuthorizedUser {
  id: string
  username: string
  token: string
  nickname?: string
  currentOrganizationMembership: OrganizationMembership
  organizationMemberships: OrganizationMembership[]
}

export interface OrganizationMembership {
  organizationId: string
  organizationName: string
  roles: string[]
}

export interface ImprovementContextNode {
  descriptor?: Descriptor
  statement?: Statement
  parent: ImprovementContextNode
  children?: ImprovementContextNode[]
}

export interface SelectedImprovement {
  parentId: string
  parent: DynamicDescriptor
  children: DynamicDescriptor[]
}

export interface Template {
  id: string
  title: string
  description?: string
  descriptors: DynamicDescriptor[]
}

export interface Descriptor {
  id: string
  title: string
  order: number
  descriptors?: DynamicDescriptor[]
  statements?: Statement[]
  gradientStyle?: string
  lowEval?: number
  highEval?: number
}

export interface DynamicDescriptor extends Descriptor {
  evaluatedStatementCount: ComputedRef<number>
  averageEvaluation: ComputedRef<number>
  lowestEvaluation: ComputedRef<number>
  statementCount: number
}

export interface WantedSituation {
  id?: string
  templateId: string
  purpose: string
  mandate: string
  resources: string
  deliverables: string
}

export interface EvaluationHistory {
  low: number
  high: number
}

export interface CorrelationSelection {
  title: string
  descriptorId: string
  order: number
  minEvaluation: number
  contexts: CorrelationContext[]
}

export interface CorrelationContext {
  title: string
  descriptorId: string
  order: number
  minEvaluation: number
  correlationEntries: CorrelationEntry[]
}

export interface CorrelationEntry {
  statementId: string
  evaluation: number
  title: string
  order: number
  label?: string
}

export interface CorrelationGroupPersistRequest {
  templateId: string
  correlationEntries: CorrelationEntry[]
}

export interface CorrelationModel {
  correlationId: string
  title: string
  createdDate: string
  lastModifiedDate: string
  description: string
  templateId: string
  contexts: Context[]
  improvements: Improvement[]
}

export interface Context {
  title: string
  evaluation: number
  label: string
  order: number
  children: Context[]
  referenceNodeId: string
}

export interface Worklog {
  id?: string
  improvementId?: string
  createdDate?: string
  entry: string
}

export interface Improvement {
  id?: string
  callToAction: string
  negatives: string[]
  positives: string[]
  weeks: number
  evaluation?: number
  worklogs?: Worklog[]
}

/** Form Interfaces **/
export interface ChangePasswordForm {
  oldPassword: string
  newPassword: string
  newPasswordConfirm: string
}
